import 'wavesurfer.js';

import * as React from 'react';
import uncontrollable from 'uncontrollable';
import { isFunction } from 'underscore';

import { AudioClipOffsets } from 'types';
import {
  MINIMUM_AUDIO_DURATION_MILLIS,
  UPLOAD_RECORDING_MAX_DURATION,
} from 'utils/constants';
import AudioClipperContainer, {
  ContainerProps,
  RenderProps,
} from './AudioClipperContainer';
import AudioClipperInner, {
  IAudioClipperInner,
  InnerProps,
} from './AudioClipperInner';

const { useCallback } = React;

type PickedFromContainer = Pick<
  ContainerProps,
  | 'defaultAudioClipDurationMillis'
  | 'defaultPositionMillis'
  | 'maxDurationMillis'
  | 'minDurationMillis'
  | 'onAudioProcess'
  | 'onError'
  | 'onClearError'
  | 'onPause'
  | 'onPlay'
  | 'onSeek'
  | 'onReady'
  | 'onRegionUpdate'
  | 'onPresetChange'
  | 'onStop'
  | 'playing'
  | 'region'
  | 'onMediaError'
>;

type PickedFromInner = Pick<
  InnerProps,
  | 'audioPeaks'
  | 'backend'
  | 'className'
  | 'wavesurferClassName'
  | 'controlsClassName'
  | 'disabled'
  | 'formClassName'
  | 'renderOverlay'
  | 'src'
  | 'captionsControl'
  | 'showWaveformLoader'
  | 'showMinimap'
  | 'showTimelineZoomSlider'
>;

type Props = PickedFromContainer & PickedFromInner;

export class AudioClipperComponent extends React.Component<Props> {
  public static defaultProps: Partial<Props> = {
    maxDurationMillis: UPLOAD_RECORDING_MAX_DURATION * 1000,
    minDurationMillis: MINIMUM_AUDIO_DURATION_MILLIS,
  };

  private inner: IAudioClipperInner;
  private container: AudioClipperContainer;

  public get clip(): AudioClipOffsets {
    return this.inner.clip;
  }

  public pause() {
    this.container.pause();
  }

  public play() {
    this.container.play();
  }

  public playSelection() {
    this.container.playSelection();
  }

  public seek(millis: number) {
    this.container.seekTo(millis);
  }

  private renderInner = (props: RenderProps) => {
    const {
      audioPeaks,
      backend,
      className,
      wavesurferClassName,
      controlsClassName,
      disabled,
      formClassName,
      captionsControl,
      renderOverlay,
      src,
      minDurationMillis,
      maxDurationMillis,
      onMediaError,
      showWaveformLoader,
      showMinimap,
      showTimelineZoomSlider,
    } = this.props;

    return (
      <AudioClipperInner
        {...props}
        audioPeaks={audioPeaks}
        backend={backend}
        className={className}
        wavesurferClassName={wavesurferClassName}
        controlsClassName={controlsClassName}
        disabled={disabled}
        formClassName={formClassName}
        captionsControl={captionsControl}
        renderOverlay={renderOverlay}
        src={src}
        minDurationMillis={minDurationMillis}
        maxDurationMillis={maxDurationMillis}
        ref={el => (this.inner = el)}
        onError={onMediaError}
        showWaveformLoader={showWaveformLoader}
        showMinimap={showMinimap}
        showTimelineZoomSlider={showTimelineZoomSlider}
      />
    );
  };

  public render() {
    const {
      defaultAudioClipDurationMillis,
      defaultPositionMillis,
      maxDurationMillis,
      minDurationMillis,
      onAudioProcess,
      onPresetChange,
      onError,
      onClearError,
      onPause,
      onPlay,
      onSeek,
      onReady,
      onRegionUpdate,
      onStop,
      playing,
      region,
    } = this.props;

    return (
      <AudioClipperContainer
        ref={el => (this.container = el)}
        render={this.renderInner}
        {...{
          defaultAudioClipDurationMillis,
          defaultPositionMillis,
          maxDurationMillis,
          minDurationMillis,
          onAudioProcess,
          onPresetChange,
          onClearError,
          onError,
          onPause,
          onPlay,
          onSeek,
          onReady,
          onRegionUpdate,
          onStop,
          playing,
          region,
        }}
      />
    );
  }
}

export type AudioClipperProps = Props & {
  defaultRegion?: Props['region'];
};

export type UncontrolledAudioClipper = React.FC<AudioClipperProps>;
const UncontrolledAudioClipperComponent = uncontrollable(
  AudioClipperComponent,
  {
    region: 'onRegionUpdate',
  },
);

const ForwardedAudioClipper = React.forwardRef<
  AudioClipperComponent,
  AudioClipperProps
>((props, ref: any) => {
  const handleUncontrolledRef = useCallback(
    (el: any) => {
      if (ref) {
        if (isFunction(ref)) {
          ref(el?.inner);
        } else {
          ref.current = el?.inner;
        }
      }
    },
    [ref],
  );

  return (
    <UncontrolledAudioClipperComponent {...props} ref={handleUncontrolledRef} />
  );
});

const AudioClipper = ForwardedAudioClipper;

export default AudioClipper;
